import React, { useState, useEffect } from 'react'
import CMS from '../../components/cms/CMS'
import { Row, Col, Card, Statistic, Spin, Alert, DatePicker, Button, Tabs, Table, Tooltip } from 'antd'
import { UserOutlined, EyeOutlined, FieldTimeOutlined, RiseOutlined, LinkOutlined, GlobalOutlined, ReloadOutlined, FileTextOutlined, LineChartOutlined } from '@ant-design/icons'
import axios from 'axios'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts'

import WorldMap from 'react-svg-worldmap'
const { RangePicker } = DatePicker
const { TabPane } = Tabs

const HomePage = () => {
    const { t } = useTranslation()
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [analytics, setAnalytics] = useState(null)
    const [dateRange, setDateRange] = useState([moment().subtract(7, 'days'), moment()])

    const fetchAnalytics = async () => {
        setLoading(true)
        setError(null)
        try {
            // Simüle edilmiş API çağrısı - doğrudan mock veri kullanılıyor
            setTimeout(() => {
                setAnalytics(generateMockData())
                setLoading(false)
            }, 500)
        } catch (err) {
            console.error('Error fetching analytics:', err)
            setError(err.message || 'Failed to load analytics data')
            setAnalytics(generateMockData())
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchAnalytics()
    }, [])

    const handleDateRangeChange = (dates) => {
        if (dates && dates.length === 2) {
            setDateRange(dates)
        }
    }

    const generateMockData = () => {
        // Tüm veriler sıfır olarak ayarlandı
        const dailyData = []
        for (let i = 6; i >= 0; i--) {
            const date = moment().subtract(i, 'days').format('MMM DD')
            dailyData.push({
                date,
                users: 0,
                pageViews: 0,
                sessions: 0
            })
        }

        return {
            totalUsers: 0,
            pageViews: 0,
            sessions: 0,
            avgSessionDuration: 0,
            bounceRate: 0,
            dailyData,
            topPages: [
                { page: '/home', views: 0, percentage: 0 },
                { page: '/products', views: 0, percentage: 0 },
                { page: '/about', views: 0, percentage: 0 },
                { page: '/contact', views: 0, percentage: 0 },
                { page: '/blog', views: 0, percentage: 0 }
            ],
            topReferrers: [
                { source: 'Google', visits: 0, percentage: 0 },
                { source: 'Direct', visits: 0, percentage: 0 },
                { source: 'Facebook', visits: 0, percentage: 0 },
                { source: 'Twitter', visits: 0, percentage: 0 },
                { source: 'LinkedIn', visits: 0, percentage: 0 }
            ],
            userLocations: [
                { country: 'US', count: 0, percentage: 0 },
                { country: 'TR', count: 0, percentage: 0 },
                { country: 'GB', count: 0, percentage: 0 },
                { country: 'DE', count: 0, percentage: 0 },
                { country: 'FR', count: 0, percentage: 0 },
                { country: 'CA', count: 0, percentage: 0 },
                { country: 'AU', count: 0, percentage: 0 }
            ]
        }
    }

    const formatNumber = (num) => {
        return num?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    }

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60)
        const secs = Math.round(seconds % 60)
        return `${mins}:${secs < 10 ? '0' + secs : secs}`
    }

    const getMapData = () => {
        return (analytics?.userLocations || []).map(location => ({
            country: location.country,
            value: location.count
        }))
    }

    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8']

    const pageColumns = [
        {
            title: t('page'),
            dataIndex: 'page',
            key: 'page',
            render: (text) => <Tooltip title={text}><span>{text}</span></Tooltip>
        },
        {
            title: t('views'),
            dataIndex: 'views',
            key: 'views',
            render: (views) => formatNumber(views)
        },
        {
            title: t('percentage'),
            dataIndex: 'percentage',
            key: 'percentage',
            render: (pct) => `${pct.toFixed(1)}%`
        }
    ]

    const referrerColumns = [
        {
            title: t('source'),
            dataIndex: 'source',
            key: 'source'
        },
        {
            title: t('visits'),
            dataIndex: 'visits',
            key: 'visits',
            render: (visits) => formatNumber(visits)
        },
        {
            title: t('percentage'),
            dataIndex: 'percentage',
            key: 'percentage',
            render: (pct) => `${pct.toFixed(1)}%`
        }
    ]

    return (
        <CMS>
            <div className="analytics-dashboard my-5">
                <div className="dashboard-header">
                    <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '24px' }}>
                        <LineChartOutlined style={{ marginRight: '8px' }} />
                        {t('analytics_dashboard')}
                    </h1>

                    <Row gutter={16} align="middle" style={{ marginBottom: '24px' }}>
                        <Col xs={24} sm={12} md={12}>
                            <RangePicker
                                value={dateRange}
                                onChange={handleDateRangeChange}
                                style={{ marginRight: '16px' }}
                            />
                            <Button
                                type="primary"
                                icon={<ReloadOutlined />}
                                onClick={fetchAnalytics}
                                style={{ marginTop: '8px', marginBottom: '8px' }}
                            >
                                {t('update')}
                            </Button>
                        </Col>
                    </Row>
                </div>

                {error && (
                    <Alert
                        message={t('error')}
                        description={error}
                        type="error"
                        closable
                        style={{ marginBottom: '24px' }}
                    />
                )}

                {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
                        <Spin size="large" />
                    </div>
                ) : (
                    <>
                        {/* Overview Cards */}
                        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                            <Col xs={24} sm={12} md={6}>
                                <Card hoverable>
                                    <Statistic
                                        title={t('total_users')}
                                        value={analytics?.totalUsers}
                                        formatter={(value) => formatNumber(value)}
                                        prefix={<UserOutlined />}
                                        valueStyle={{ color: '#1890ff' }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                                <Card hoverable>
                                    <Statistic
                                        title={t('page_views')}
                                        value={analytics?.pageViews}
                                        formatter={(value) => formatNumber(value)}
                                        prefix={<EyeOutlined />}
                                        valueStyle={{ color: '#52c41a' }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                                <Card hoverable>
                                    <Statistic
                                        title={t('avg_session_time')}
                                        value={analytics?.avgSessionDuration}
                                        formatter={(value) => formatTime(value)}
                                        prefix={<FieldTimeOutlined />}
                                        valueStyle={{ color: '#722ed1' }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                                <Card hoverable>
                                    <Statistic
                                        title={t('bounce_rate')}
                                        value={analytics?.bounceRate}
                                        precision={1}
                                        suffix="%"
                                        prefix={<RiseOutlined />}
                                        valueStyle={{ color: analytics?.bounceRate > 50 ? '#ff4d4f' : '#faad14' }}
                                    />
                                </Card>
                            </Col>
                        </Row>

                        {/* Traffic Over Time */}
                        <Card title={t('traffic_over_time')} style={{ marginBottom: '24px' }}>
                            <ResponsiveContainer width="100%" height={300}>
                                <AreaChart
                                    data={analytics?.dailyData}
                                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="date" />
                                    <YAxis />
                                    <RechartsTooltip />
                                    <Legend />
                                    <Area
                                        type="monotone"
                                        dataKey="users"
                                        name={t('users')}
                                        stroke="#8884d8"
                                        fill="#8884d8"
                                        fillOpacity={0.3}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="pageViews"
                                        name={t('page_views')}
                                        stroke="#82ca9d"
                                        fill="#82ca9d"
                                        fillOpacity={0.3}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="sessions"
                                        name={t('sessions')}
                                        stroke="#ffc658"
                                        fill="#ffc658"
                                        fillOpacity={0.3}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </Card>

                        {/* Detail Tabs */}
                        <Tabs defaultActiveKey="pages" size="large">
                            {/* Top Pages */}
                            <TabPane
                                tab={<span><FileTextOutlined className='mr-2' />{t('top_pages')}</span>}
                                key="pages"
                            >
                                <Row gutter={[16, 16]}>
                                    <Col xs={24} md={14}>
                                        <Table
                                            dataSource={analytics?.topPages}
                                            columns={pageColumns}
                                            pagination={false}
                                            rowKey="page"
                                        />
                                    </Col>
                                    <Col xs={24} md={10}>
                                        <ResponsiveContainer width="100%" height={300}>
                                            <PieChart>
                                                <Pie
                                                    data={analytics?.topPages}
                                                    innerRadius={70}
                                                    outerRadius={90}
                                                    fill="#8884d8"
                                                    paddingAngle={5}
                                                    dataKey="views"
                                                    nameKey="page"
                                                    label={({ page, percentage }) => `${page}: ${percentage.toFixed(1)}%`}
                                                >
                                                    {analytics?.topPages.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                    ))}
                                                </Pie>
                                                <RechartsTooltip formatter={(value) => formatNumber(value)} />
                                                <Legend />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </Col>
                                </Row>
                            </TabPane>

                            {/* Top Referrers */}
                            <TabPane
                                tab={<span><LinkOutlined className='mr-2' />{t('top_referrers')}</span>}
                                key="referrers"
                            >
                                <Row gutter={[16, 16]}>
                                    <Col xs={24} md={14}>
                                        <Table
                                            dataSource={analytics?.topReferrers}
                                            columns={referrerColumns}
                                            pagination={false}
                                            rowKey="source"
                                        />
                                    </Col>
                                    <Col xs={24} md={10}>
                                        <ResponsiveContainer width="100%" height={300}>
                                            <BarChart
                                                data={analytics?.topReferrers}
                                                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="source" />
                                                <YAxis />
                                                <RechartsTooltip formatter={(value) => formatNumber(value)} />
                                                <Bar dataKey="visits" name={t('visits')} fill="#8884d8">
                                                    {analytics?.topReferrers.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                    ))}
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </Col>
                                </Row>
                            </TabPane>

                            {/* User Locations */}
                            <TabPane
                                tab={<span><GlobalOutlined className='mr-2' />{t('user_locations')}</span>}
                                key="locations"
                            >
                                <Row gutter={[16, 16]}>
                                    <Col xs={24} md={14}>
                                        <div style={{ height: 400 }}>
                                            <WorldMap
                                                color="blue"
                                                title={t('user_distribution')}
                                                value-suffix="users"
                                                size="responsive"
                                                data={getMapData()}
                                            />
                                        </div>
                                    </Col>
                                    <Col xs={24} md={10}>
                                        <Table
                                            dataSource={analytics?.userLocations}
                                            columns={[
                                                {
                                                    title: t('country'),
                                                    dataIndex: 'country',
                                                    key: 'country'
                                                },
                                                {
                                                    title: t('users'),
                                                    dataIndex: 'count',
                                                    key: 'count',
                                                    render: (count) => formatNumber(count)
                                                },
                                                {
                                                    title: t('percentage'),
                                                    dataIndex: 'percentage',
                                                    key: 'percentage',
                                                    render: (pct) => `${pct.toFixed(1)}%`
                                                }
                                            ]}
                                            pagination={false}
                                            rowKey="country"
                                        />
                                    </Col>
                                </Row>
                            </TabPane>
                        </Tabs>
                    </>
                )}
            </div>
        </CMS>
    )
}

export default HomePage